import { isArray, isObject, isString, upperFirst } from 'lodash-es';
import type { EmptyObject, IsNever, RequireExactlyOne, SetRequired } from 'type-fest';
import { validate as isValidUUID } from 'uuid';
import { objectEntries, objectKeys } from './object';
import { singular } from 'pluralize';

export type Entity = Record<string, any>;

type QueryParams = {
    filter?: string;
    select?: string;
    orderby?: string;
    top?: number;
    skip?: number;
    count?: boolean;
    expand?: string;
};

export const oDataParamsInOrder = ['filter', 'select', 'orderby', 'top', 'skip', 'count', 'expand'] as const;

type ParamKeysToTypeArray<T, $res extends unknown[] = []> = T extends readonly [infer First extends keyof QueryParams, ...infer Rest extends (keyof QueryParams)[]]
    ? ParamKeysToTypeArray<Rest, [...$res, QueryParams[First]]>
    : $res;

type GetAllArgs = ParamKeysToTypeArray<typeof oDataParamsInOrder>;

const buildQueryArray = (params: QueryParams) => oDataParamsInOrder.map(k => params[k]) as GetAllArgs;

// Note: OData does not support excluding fields, therefore we use `true` instead of a boolean in the select.
/** Exported only for supporting Angular compatibility. */
export type SelectConfig<E extends Entity> = { [K in keyof E]?: true };

type Select<E extends Entity, S extends SelectConfig<E> | unknown, R extends Relations<Entity>> = unknown extends S ? E : { [K in keyof S & keyof E]: K extends keyof NonNullable<Relations<E>> ? K extends keyof R ? E[K] : never : E[K] };

const selectConfigToStr = (params: SelectConfig<Entity>): string => Object.keys(params).filter(k => params[k]).map(k => upperFirst(k)).join(',');

type NonEmptyArray<T> = [T, ...T[]];
type AtLeastTwoArray<T> = [T, T, ...T[]];

type OrderByConfig<E extends Entity> = Readonly<NonEmptyArray<keyof E & string | [keyof E & string, 'asc' | 'desc']>>;

const orderByConfigToStr = <const E extends Entity>(params: OrderByConfig<E>): string => params.map(param => {
    if (!isArray(param)) return upperFirst(param.toString());
    const [key, dir] = param;
    return upperFirst(key.toString()) + (dir ? ` ${dir}` : '');
}).join(',');

const FILTER_PRIMITIVE_ARRAY_KEY = '$item' as const;

type Ops<T> =
    T extends (infer I)[]
    ? I extends Entity
    ? { any: FilterConfig<I>, all: FilterConfig<I> }
    : { any: FilterConfig<{ [FILTER_PRIMITIVE_ARRAY_KEY]: I }>, all: FilterConfig<{ [FILTER_PRIMITIVE_ARRAY_KEY]: I }> }
    : T extends number
    ? { eq: T, ne: T, gt: T, ge: T, lt: T, le: T, in: T[] }
    : T extends string
    ? { eq: T, ne: T, contains: T, in: T[] }
    : { eq: T, ne: T };

type CustomFilters<E extends Entity> = { and: AtLeastTwoArray<FilterConfig<E>>, or: AtLeastTwoArray<FilterConfig<E>>, not: FilterConfig<E> };
type EntityFilters<E extends Entity> = {
    [K in keyof E & string]: NonNullable<E[K]> extends any[]
    ? RequireExactlyOne<Ops<NonNullable<E[K]>>>
    : NonNullable<E[K]> extends Entity
    ? FilterConfig<NonNullable<E[K]>>
    : RequireExactlyOne<Ops<E[K]>>
};
type FilterConfig<E extends Entity> = RequireExactlyOne<EntityFilters<E> & CustomFilters<E>>;

const filterConfigToStr = <const E extends Entity>(params: FilterConfig<E>, path: string = ''): string => {
    const { and, or, not, ...rest } = params as EntityFilters<E> & CustomFilters<E>;
    if (and !== void 0) return `(${and.map(x => filterConfigToStr(x, path)).join(' and ')})`;
    if (or !== void 0) return `(${or.map(x => filterConfigToStr(x, path)).join(' or ')})`;
    if (not !== void 0) return `(not ${filterConfigToStr(not, path)})`;
    const entityFilters = objectEntries(rest);
    const [kV] = entityFilters;
    if (entityFilters.length !== 1 || kV === void 0) throw new Error('Only one key allowed in object filter');
    const [k, v] = kV;
    if (!isObject(v)) throw new Error('Filters must be objects');
    const keys = objectKeys(v);
    const [key] = keys;
    if (keys.length !== 1 || key === void 0) throw new Error('Only one key allowed in object filter');
    const rawValue = v[key];
    if (key === 'any' || key === 'all') {
        if (!isObject(rawValue)) throw new Error('Value of any/all filter should be an object');
        const [predicateKey] = objectKeys(rawValue);
        if (!predicateKey) throw new Error(`Invalid ${key} filter: missing predicate`);
        const arg = singular(`${path.replace('/', '_')}${upperFirst(k.toString())}`);
        const predicateStr = predicateKey === FILTER_PRIMITIVE_ARRAY_KEY
            ? filterConfigToStr({ [arg]: rawValue[predicateKey] } as FilterConfig<E>)
            : filterConfigToStr(rawValue as FilterConfig<E[typeof k]>, `${arg}/`);
        const pascalK = `${path}${upperFirst(k.toString())}`;
        return `${pascalK}/${key}(${arg}: ${predicateStr})`;
    }
    if (isObject(rawValue) && !isArray(rawValue)) return filterConfigToStr(v as FilterConfig<E[typeof k]>, `${path}${upperFirst(k.toString())}/`);
    const getValue = (rawValue: unknown): string => {
        if (isArray(rawValue)) return `(${rawValue.map(x => getValue(x)).join(',')})`;
        if (isString(rawValue) && !isValidUUID(rawValue)) return `'${rawValue}'`;
        return `${rawValue}`;
    };
    const value = getValue(rawValue);
    const pascalK = `${path}${upperFirst(k.toString())}`;
    switch (key) {
        case 'contains': return `contains(${pascalK}, ${value})`;
        default: return `${pascalK} ${key.toString()} ${value}`;
    }
};

/**
 * Exported only for supporting Angular compatibility.
 *
 * Given an entity type E returns an object consisting of its relations (recursively).
 * 
 * Note: Currently there is no max depth of the recursion, might need to be added.
 * 
 * Assumption: relation key = relation object + 'Id' suffix, 
 * e.g. relation key = networkId, where relation object = network
 * 
 * Assumption: If property is nullable and an array of objects,
 * then the property is treated as a relation.
 */
export type Relations<E extends Entity> = {
    [K in keyof E & string as `${K}Id` extends keyof E & string
    ? K
    : undefined extends E[K]
    ? NonNullable<E[K]> extends (infer Arr)[]
    ? Arr extends Entity
    ? K
    : never
    : never
    : never
    ]?: (NonNullable<E[K]> extends (infer Arr)[] ? Arr extends Entity ? Arr : never : NonNullable<E[K]>) extends infer U extends Entity
    ? (Relations<U> & {
        select?: SelectConfig<U>,
        orderBy?: OrderByConfig<U>,
        filter?: FilterConfig<U>,
    }) | true
    : never
};

export type RelationKeys<E extends Entity> = keyof Relations<E>;

/**
 * Given an entity type E and a relations object R, 
 * returns an object consisting of the non-relation properties of E 
 * and the expanded relations based on R.
 */
export type Expand<E extends Entity, R extends Relations<E> = EmptyObject> = {
    [K in keyof E & string as K extends RelationKeys<E> ? K extends keyof R & string ? K : never : K]: K extends keyof R & string
    ? `${K}Id` extends keyof E & string
    ? null extends E[`${K}Id`]
    ? Expand<Select<NonNullable<E[K]>, NonNullable<R[K]> extends { select: unknown } ? NonNullable<R[K]>['select'] : unknown, NonNullable<R[K]>>, NonNullable<R[K]>> | null
    : Expand<Select<NonNullable<E[K]>, NonNullable<R[K]> extends { select: unknown } ? NonNullable<R[K]>['select'] : unknown, NonNullable<R[K]>>, NonNullable<R[K]>>
    : null extends E[K]
    ? NonNullable<E[K]> extends (infer ArrElem)[]
    ? ArrElem extends Entity
    ? Expand<Select<ArrElem, NonNullable<R[K]> extends { select: unknown } ? NonNullable<R[K]>['select'] : unknown, NonNullable<R[K]>>, NonNullable<R[K]>>[]
    : Exclude<E[K], undefined>
    : Exclude<E[K], undefined>
    : Exclude<E[K], undefined>
    : Exclude<E[K], undefined>
};

/**
 * Given a relations object, returns a string (used in OData requests) 
 * consisting of the relations expanded.
 */
const relationsToExpand = (relations: Relations<Entity>): string => {
    return objectEntries(relations).map(([k, v]) => {
        const pascalK = upperFirst(k.toString());
        if (!isObject(v) || Object.keys(v).length === 0) return pascalK;
        const { select, orderBy, filter, ...nestedRelations } = v;
        const expand = Object.keys(nestedRelations).length === 0 ? void 0 : `$expand=${relationsToExpand(nestedRelations)}`;
        const selectStr = select !== void 0 ? `$select=${selectConfigToStr(select)}` : void 0;
        const orderByStr = orderBy !== void 0 ? `$orderby=${orderByConfigToStr(orderBy)}` : void 0;
        const filterStr = filter !== void 0 ? `$filter=${filterConfigToStr(filter)}` : void 0;
        const nested = [expand, selectStr, orderByStr, filterStr].filter(x => x !== void 0).join(';');
        return `${pascalK}(${nested})`;
    }).join(',');
};

type ODataResponse<E extends Entity> = { "@odata.count"?: number; value: E[] };
export type ODataClient<E extends Entity> = { get: (...args: GetAllArgs) => PromiseLike<ODataResponse<E>> };

type GetRawParamsHelper<
    E extends Entity,
    R extends Relations<E> | EmptyObject,
    S extends SelectConfig<Expand<E, R>>
> = Pick<QueryParams, 'top' | 'skip' | 'count'> & {
    expand?: R,
    select?: S,
    orderBy?: OrderByConfig<E>,
    filter?: FilterConfig<E>,
};


/** Exported only for supporting Angular compatibility. */
export type GetPaginatedParamsHelper<
    E extends Entity,
    R extends Relations<E> | EmptyObject,
    S extends SelectConfig<Expand<E, R>>
> = Omit<GetRawParamsHelper<E, R, S>, 'count'>;

/** Exported only for supporting Angular compatibility. */
export type GetManyParamsHelper<
    E extends Entity,
    R extends Relations<E> | EmptyObject,
    S extends SelectConfig<Expand<E, R>>
> = Omit<GetRawParamsHelper<E, R, S>, 'count'>;

/** Exported only for supporting Angular compatibility. */
export type GetOneParamsHelper<
    E extends Entity,
    R extends Relations<E> | EmptyObject,
    S extends SelectConfig<Expand<E, R>>
> = SetRequired<Pick<GetRawParamsHelper<E, R, S>, 'expand' | 'select' | 'filter'>, 'filter'>;

export type ODataGetPaginatedParams<E extends Entity> = GetPaginatedParamsHelper<E, Relations<E>, SelectConfig<Expand<E, Relations<E>>>>;
export type ODataGetManyParams<E extends Entity> = GetManyParamsHelper<E, Relations<E>, SelectConfig<Expand<E, Relations<E>>>>;
export type ODataGetOneParams<E extends Entity> = GetOneParamsHelper<E, Relations<E>, SelectConfig<Expand<E, Relations<E>>>>;

export type ODataGetPaginated<E extends Entity, P extends ODataGetManyParams<E> = {}> = { value: ODataGetMany<E, P>; count: number };
export type ODataGetMany<E extends Entity, P extends ODataGetManyParams<E> = {}> = Select<Expand<E, NonNullable<P['expand']>>, P['select'], NonNullable<P['expand']>>[];
export type ODataGetOne<E extends Entity, P extends ODataGetManyParams<E> = {}> = ODataGetMany<E, P>[number] | null;

/**
 * Executes an OData GET request with the specified query parameters and relation expansions.
 * 
 * **Important:** The function may not always accurately detect which properties can be expanded.
 * In such cases, review the assumptions made by the `Relations` type for potential adjustments.
 * 
 * @template E - The entity type representing the main data model.
 * @template R - The relations to expand, derived from the entity type (optional).
 * @template S - The fields to select, derived from the entity and expanded relations (optional).
 * 
 * @param client - The client object with a `get` method that performs the OData query.
 * @param params - The query parameters:
 *   - `expand` (optional): Specifies relations to expand.
 *   - `select` (optional): Specifies fields to include in the result.
 *   - `orderBy` (optional): Specifies the sorting configuration for the query.
 *   - `filter` (optional): Specifies the filter configuration to apply to the query.
 *   - Additional OData query parameters, such as `top`, `skip`, and `count` can also be included.
 * 
 * @returns An array of entities matching the query parameters, with correctly expanded relations and selected fields.
 */
export const getManyUsingOData = async <
    const E extends Entity,
    const R extends Relations<E> | EmptyObject,
    const S extends SelectConfig<Expand<E, R>>,
>(
    client: ODataClient<E>,
    params: GetManyParamsHelper<E, R, S> = {}
) => (await getRawUsingOData(client, params)).value;

/**
 * Fetches a single entity using OData query parameters.
 * 
 * - Automatically appends `$top=1` to the query, ensuring only one result is returned.
 * - Infers and returns the result as a single object instead of an array.
 * 
 * @template E - The entity type representing the main data model.
 * @template R - The relations to expand, derived from the entity type (optional).
 * @template S - The fields to select, derived from the entity and expanded relations (optional).
 * 
 * @param client - The client object with a `get` method that executes OData queries.
 * @param params - The query parameters:
 *   - `expand` (optional): Specifies relations to expand.
 *   - `select` (optional): Specifies fields to include in the result.
 *   - `filter`: Specifies the filter configuration to apply to the query.
 * 
 * @returns The single entity matching the query parameters.
 */
export const getOneUsingOData = async <
    const E extends Entity,
    const R extends Relations<E> | EmptyObject,
    const S extends SelectConfig<Expand<E, R>>,
>(
    client: ODataClient<E>,
    params: GetOneParamsHelper<E, R, S>,
) => {
    if (params.filter === void 0) throw new Error(`@ccq/ts-lib getOneUsingOData: Expected filter to be defined`);
    const { select, expand, filter } = params;
    const { value } = await getRawUsingOData(client, { select, expand, filter, top: 1 });
    if (value.length > 1) throw new Error(`@ccq/ts-lib getOneUsingOData: Expected one or zero reults, got ${value.length}`);
    const [result] = value;
    return (result ?? null) as NonNullable<typeof result> | null;
}

export const getPaginatedUsingOData = async <
    const E extends Entity,
    const R extends Relations<E> | EmptyObject,
    const S extends SelectConfig<Expand<E, R>>,
>(
    client: ODataClient<E>,
    params: GetPaginatedParamsHelper<E, R, S> = {},
) => {
    const { value, count } = await getRawUsingOData(client, { ...params, count: true });
    if (count === void 0) throw new Error(`@ccq/ts-lib getPaginatedUsingOData: Expected count to be defined`);
    return { value, count };
}

const getRawUsingOData = async <
    const E extends Entity,
    const R extends Relations<E> | EmptyObject,
    const S extends SelectConfig<Expand<E, R>>,
>(
    client: ODataClient<E>,
    params: GetRawParamsHelper<E, R, S>
) => {
    const { expand, select, orderBy, filter, ...restParams } = params;
    const expandStr = expand !== void 0 ? relationsToExpand(expand as R) : void 0;
    const selectStr = select !== void 0 ? selectConfigToStr(select as S) : void 0;
    const orderByStr = orderBy !== void 0 ? orderByConfigToStr(orderBy as OrderByConfig<E>) : void 0;
    const filterStr = filter !== void 0 ? filterConfigToStr(filter as FilterConfig<E>) : void 0;
    const queryArray = buildQueryArray({ ...restParams, expand: expandStr, select: selectStr, orderby: orderByStr, filter: filterStr });
    const { value, "@odata.count": count } = await client.get(...queryArray);
    return { value: value as Select<Expand<E, R>, S, R>[], count };
};

/**
 * @deprecated Use `getOneUsingOData` instead
 */
export const getUsingODataSingle = getOneUsingOData;

/**
 * @deprecated Use `getManyUsingOData` instead
 */
export const getUsingOData = getManyUsingOData;

/**
 * @deprecated Use `ODataGetManyParams` instead
 */
export type ODataGetParams<E extends Entity> = ODataGetManyParams<E>;

/**
 * @deprecated Use `ODataGetOneParams` instead
 */
export type ODataGetSingleParams<E extends Entity> = ODataGetOneParams<E>;

/**
 * @deprecated Use `ODataGetMany` instead
 */
export type ODataGet<E extends Entity, P extends ODataGetManyParams<E> = {}> = ODataGetMany<E, P>;

/**
 * @deprecated Use `ODataGetOne` instead
 */
export type ODataGetSingle<E extends Entity, P extends ODataGetManyParams<E> = {}> = ODataGetOne<E, P>;

export type ODataGetType = 'one' | 'many' | 'paginated';

type GetODataParams<E extends Entity, Type extends ODataGetType> = Type extends 'one' ? ODataGetOneParams<E> : Type extends 'many' ? ODataGetManyParams<E> : Type extends 'paginated' ? ODataGetPaginatedParams<E> : never;

export type QueryKeyCrud<E extends Entity, Type extends ODataGetType = never, A extends GetODataParams<E, Type> = never> =
    IsNever<Type> extends true
    ? readonly [ODataClient<E>, 'get']
    : IsNever<A> extends true
    ? readonly [ODataClient<E>, 'get', Type]
    : readonly [ODataClient<E>, 'get', Type, A];

/**
 * Defines the structure for a query key and function for CRUD operations in an OData client.
 *
 * @template Type - Determines whether the operation is for a single entity ('one') / multiple entities ('many') / paginated results ('paginated').
 * @template E - The entity type being queried.
 * @template A - The query parameters, which depend on whether the query is for a single ('one') or multiple ('many') entities.
 */
export type QueryKeyAndFnCrud<E extends Entity, Type extends ODataGetType, A extends GetODataParams<E, Type>> = {
    queryKey: QueryKeyCrud<E, Type, A>;
    queryFn: () => Promise<Type extends 'one' ? ODataGetOne<E, A> : Type extends 'many' ? ODataGetMany<E, A> : Type extends 'paginated' ? ODataGetPaginated<E, A> : never>;
}

export function queryKeyCrudGet<E extends Entity>(
    client: ODataClient<E>
): QueryKeyCrud<E>;
export function queryKeyCrudGet<E extends Entity, Type extends ODataGetType>(
    client: ODataClient<E>,
    type: Type
): QueryKeyCrud<E, Type>;
export function queryKeyCrudGet<E extends Entity, Type extends ODataGetType, A extends GetODataParams<E, Type>>(
    client: ODataClient<E>,
    type: Type,
    args: A
): QueryKeyCrud<E, Type, A>;
export function queryKeyCrudGet<E extends Entity, Type extends ODataGetType = never, A extends GetODataParams<E, Type> = never>(
    client: ODataClient<E>,
    type?: Type,
    args?: A
) {
    if (type === void 0) return [client, 'get'] as const;
    if (args === void 0) return [client, 'get', type] as const;
    return [client, 'get', type, args] as const;
}

/**
 * Creates a query key and function for retrieving a single entity.
 *
 * @template E - The entity type.
 * @template A - The query parameters for retrieving a single entity.
 *
 * @param client - The OData client instance used to make the request.
 * @param args - The parameters for fetching the entity.
 * @returns An object containing the query key and a function to fetch the entity.
 */
export const queryKeyAndFnCrudGetOne = <
    E extends Entity,
    const R extends Relations<E> | EmptyObject,
    const S extends SelectConfig<Expand<E, R>>
>(client: ODataClient<E>, args: GetOneParamsHelper<E, R, S>) => ({
    queryKey: queryKeyCrudGet(client, 'one', args),
    queryFn: () => getOneUsingOData(client, args),
});

/**
 * Creates a query key and function for retrieving multiple entities.
 *
 * @template E - The entity type.
 * @template A - The query parameters for retrieving multiple entities.
 *
 * @param client - The OData client instance used to make the request.
 * @param args - The parameters for fetching the entities.
 * @returns An object containing the query key and a function to fetch multiple entities.
 */
export const queryKeyAndFnCrudGetMany = <
    E extends Entity,
    const R extends Relations<E> | EmptyObject,
    const S extends SelectConfig<Expand<E, R>>
>(client: ODataClient<E>, args: GetManyParamsHelper<E, R, S> = {}) => ({
    queryKey: queryKeyCrudGet(client, 'many', args),
    queryFn: () => getManyUsingOData(client, args),
});

/**
 * Creates a query key and function for retrieving paginated entities.
 *
 * @template E - The entity type.
 * @template A - The query parameters for retrieving paginated entities.
 *
 * @param client - The OData client instance used to make the request.
 * @param args - The parameters for fetching the entities.
 * @returns An object containing the query key and a function to fetch paginated entities.
 */
export const queryKeyAndFnCrudGetPaginated = <
    E extends Entity,
    const R extends Relations<E> | EmptyObject,
    const S extends SelectConfig<Expand<E, R>>
>(client: ODataClient<E>, args: GetPaginatedParamsHelper<E, R, S> = {}) => ({
    queryKey: queryKeyCrudGet(client, 'paginated', args),
    queryFn: () => getPaginatedUsingOData(client, args),
});
